/**
 * Satellite NDVI Panel Component
 * 
 * Displays satellite imagery analysis as per PRD FR-003:
 * - NDVI visualization and classification (from real-time API or fallback)
 * - Crop health monitoring
 * - Time-series analysis
 * - Change detection
 * 
 * Data Source: Real-time from /api/satellite/ndvi or fallback to static data
 * 
 * UI: Modern, clean, and easy to understand with visual indicators
 */

import React, { useState, useMemo } from 'react';
import FarmMap from './FarmMap';
import {
  FaSatellite, FaLeaf, FaChartLine,
  FaInfoCircle, FaDownload, FaExclamationTriangle,
  FaArrowUp, FaArrowDown, FaSync,
  FaFilePdf
} from 'react-icons/fa';
import {
  calculateFieldNDVIStats,
  analyzeNDVITimeSeries,
  detectVegetationChange,
  NDVI_CLASSIFICATIONS,
  SATELLITE_SOURCES,
  generateSatelliteReport
} from '../services/satelliteService';
import { SATELLITE_DATA } from '../data/dashboardData';
import { fieldBoundaries } from '../data/fieldBoundaries';
import { useNDVIData, useNDVITimeSeries } from '../hooks/useRealTimeData';
import { jsPDF } from 'jspdf';
// import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import '../styles/sustainability.css';

// Fallback static data configuration
const fallbackNdviValues = SATELLITE_DATA.ndviValues;
const fallbackPreviousNDVI = SATELLITE_DATA.previousNDVI;
const fallbackTimeSeries = SATELLITE_DATA.timeSeries;

const SatelliteNDVIPanel = ({ initialFieldId }) => {
  // Field Options from GeoJSON
  const geoFieldOptions = useMemo(() => {
    return fieldBoundaries.features
      .filter(f => f.geometry.type === 'Polygon')
      .map(f => ({
        id: `field-${f.properties.id}`,
        dbId: f.properties.id,
        name: f.properties.name,
        area: f.properties.area,
        crop: f.properties.crop,
        coordinates: f.geometry.coordinates[0][0] // Simple center proxy
      }));
  }, []);

  const [selectedField, setSelectedField] = useState(
    initialFieldId ? `field-${initialFieldId}` : geoFieldOptions[0]?.id
  );

  const currentFieldInfo = useMemo(() => {
    return geoFieldOptions.find(f => f.id === selectedField) || geoFieldOptions[0];
  }, [selectedField, geoFieldOptions]);

  const [lat, lon] = currentFieldInfo.coordinates;
  const [activeView, setActiveView] = useState('current');
  const [isRefreshing] = useState(false);

  // handleDownloadReport declaration
  const handleDownloadReport = () => {
    const doc = new jsPDF();
    const report = generateSatelliteReport(currentFieldInfo, realTimeNDVI || { ndviData: { mean: 0.45 } });

    doc.setFontSize(22);
    doc.text("Satellite NDVI Analysis", 20, 20);
    doc.setFontSize(12);
    doc.text(`Field: ${report.field.name}`, 20, 40);
    doc.text(`Crop: ${report.field.crop}`, 20, 50);
    doc.text(`Mean NDVI: ${report.vegetation.ndviMean}`, 20, 60);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 20, 70);

    doc.save(`${report.field.name}-NDVI-Report.pdf`);
  };

  // Fetch real-time NDVI data
  const {
    data: realTimeNDVI,
    refetch: refetchNDVI,
    lastUpdated: ndviUpdated
  } = useNDVIData(lat, lon, 25);

  // Fetch real-time time series
  const {
    data: realTimeTimeSeries
  } = useNDVITimeSeries(lat, lon, 90);

  // Get NDVI values from API or fallback
  const ndviValues = useMemo(() => {
    if (realTimeNDVI?.fields) {
      const field = realTimeNDVI.fields.find(f => f.field_id === selectedField);
      if (field) {
        // Generate array of NDVI values around the mean
        const mean = field.mean_ndvi;
        const std = field.std_ndvi;
        return Array.from({ length: 100 }, () =>
          Math.max(0, Math.min(1, mean + (Math.random() - 0.5) * std * 2))
        );
      }
    }
    return fallbackNdviValues;
  }, [realTimeNDVI, selectedField]);

  // Get time series from API or fallback
  const timeSeries = useMemo(() => {
    if (realTimeTimeSeries?.time_series) {
      return realTimeTimeSeries.time_series.map(ts => ({
        date: ts.date,
        ndvi: ts.ndvi_value
      }));
    }
    return fallbackTimeSeries;
  }, [realTimeTimeSeries]);

  // Calculate previous NDVI for change detection
  const previousNDVI = useMemo(() => {
    if (realTimeNDVI?.change_detection) {
      const change = realTimeNDVI.change_detection.find(c => c.field_id === selectedField);
      if (change) {
        return Array.from({ length: 100 }, () => change.previous_ndvi);
      }
    }
    return fallbackPreviousNDVI;
  }, [realTimeNDVI, selectedField]);



  // Calculate NDVI statistics using data
  const fieldStats = useMemo(() => {
    return calculateFieldNDVIStats(ndviValues);
  }, [ndviValues]);

  // Analyze time series
  const trendAnalysis = useMemo(() => {
    return analyzeNDVITimeSeries(timeSeries);
  }, [timeSeries]);

  // Detect changes
  const changeDetection = useMemo(() => {
    return detectVegetationChange(previousNDVI, ndviValues);
  }, [previousNDVI, ndviValues]);

  const handleRefresh = () => {
    refetchNDVI();
  };

  return (
    <TooltipProvider>
      <div className="p-4 md:p-6 space-y-6 max-w-[1600px] mx-auto pb-24 font-sans text-text-primary">
        {/* Header Section */}
        <div className="bg-surface-card border border-border-strong rounded-xl p-6 shadow-sm">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-primary/10 border border-primary/20">
                <FaSatellite className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight uppercase text-text-primary">Satellite Imagery Analysis</h1>
                <p className="text-sm text-text-secondary font-mono mt-1">
                  NDVI-based crop health monitoring • Sentinel-2 • Landsat 8
                </p>
                <div className="flex items-center gap-3 mt-3 text-xs font-mono uppercase tracking-wide">
                  <span className={`px-2 py-0.5 rounded flex items-center gap-2 ${realTimeNDVI ? 'bg-primary/20 text-primary border border-primary/30' : 'bg-secondary text-text-muted border border-border-subtle'}`}>
                    {isRefreshing ? (
                      <>
                        <FaSync className="w-3 h-3 animate-spin" />
                        SYNCING...
                      </>
                    ) : (
                      <>
                        <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                        {realTimeNDVI ? 'LIVE FEED ACTIVE' : 'DEMO MODE'}
                      </>
                    )}
                  </span>
                  {ndviUpdated && (
                    <span className="text-text-muted">
                      LAST SAT PASS: {ndviUpdated.toLocaleTimeString()}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-end gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] uppercase font-bold text-text-muted tracking-wider">Target Field</label>
                <div className="relative">
                  <select
                    value={selectedField}
                    onChange={(e) => setSelectedField(e.target.value)}
                    className="appearance-none bg-secondary border border-border-strong text-text-primary text-sm rounded-lg px-4 py-2 pr-8 min-w-[240px] focus:outline-none focus:border-primary/50 transition-colors cursor-pointer font-mono"
                  >
                    {geoFieldOptions.map(field => (
                      <option key={field.id} value={field.id}>
                        {field.name} ({field.area}ac)
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-text-muted">
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="h-[38px] mt-auto border-border-strong hover:bg-secondary hover:text-white"
              >
                <FaSync className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                variant="outline"
                className="gap-2 border-border-strong text-text-primary hover:bg-secondary"
                onClick={handleDownloadReport}
              >
                <FaFilePdf className="w-3.5 h-3.5" />
                Report
              </Button>
            </div>
          </div>
        </div>

        {/* Satellite Sources */}
        {/* Satellite Sources Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(SATELLITE_SOURCES).map(([key, source]) => (
            <div key={key} className="bg-surface-card border border-border-strong rounded-xl p-4 flex items-center justify-between shadow-sm">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <FaSatellite className="w-4 h-4 text-primary" />
                  <span className="font-bold text-text-primary tracking-tight">{source.name}</span>
                </div>
                <div className="text-xs text-text-secondary font-mono">
                  {source.provider} • Update: {source.updateFrequency}
                </div>
              </div>
              <div className="bg-secondary text-text-primary text-[10px] font-bold px-2 py-1 rounded border border-border-subtle uppercase tracking-wider">
                RES: {source.resolution}
              </div>
            </div>
          ))}
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeView} onValueChange={setActiveView} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="current">Current Status</TabsTrigger>
            <TabsTrigger value="trends">Trends</TabsTrigger>
            <TabsTrigger value="changes">Change Detection</TabsTrigger>
          </TabsList>

          {/* Current Status Tab */}
          <TabsContent value="current" className="space-y-6">
            {/* Main NDVI Stat Card */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1 space-y-6">
                {/* Map Preview */}
                <div className="bg-surface-card border border-border-strong rounded-xl p-1 shadow-sm overflow-hidden">
                  <FarmMap
                    className="h-64 rounded-lg"
                    mapVariant="satellite"
                    selectedFieldId={parseInt(selectedField.replace('field-', ''))}
                    onFieldSelect={() => { }}
                  />
                </div>

                <div className="bg-surface-card border border-border-strong rounded-xl p-6 shadow-sm flex flex-col items-center justify-center relative">
                  <h3 className="text-sm font-mono font-bold text-text-secondary uppercase tracking-wider mb-4 w-full text-center">Mean NDVI Index</h3>

                  <div className="relative w-40 h-40 mb-4">
                    <svg className="transform -rotate-90 w-40 h-40">
                      <circle cx="80" cy="80" r="70" stroke="var(--border-subtle)" strokeWidth="12" fill="none" />
                      <circle
                        cx="80" cy="80" r="70"
                        stroke={fieldStats.classification.color}
                        strokeWidth="12"
                        fill="none"
                        strokeDasharray={`${2 * Math.PI * 70}`}
                        strokeDashoffset={`${2 * Math.PI * 70 * (1 - fieldStats.mean)}`}
                        className="transition-all duration-500 shadow-[0_0_15px_rgba(46,204,113,0.3)]"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-5xl font-bold tracking-tighter" style={{ color: fieldStats.classification.color }}>
                        {fieldStats.mean.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className={`px-4 py-1.5 rounded-full border text-xs font-bold uppercase tracking-wider flex items-center gap-2`}
                    style={{
                      borderColor: fieldStats.classification.color,
                      color: fieldStats.classification.color,
                      backgroundColor: fieldStats.classification.color + '15'
                    }}
                  >
                    <FaLeaf className="w-3 h-3" />
                    {fieldStats.classification.label}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2 bg-surface-card border border-border-strong rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-6">
                  <FaChartLine className="w-5 h-5 text-primary" />
                  <h3 className="text-lg font-bold text-text-primary">NDVI Statistics</h3>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-secondary rounded-lg p-3 border border-border-subtle">
                    <div className="text-[10px] font-bold text-text-secondary uppercase tracking-wider mb-1">Median</div>
                    <div className="text-xl font-bold font-mono text-text-primary">{fieldStats.median.toFixed(3)}</div>
                  </div>
                  <div className="bg-secondary rounded-lg p-3 border border-border-subtle">
                    <div className="text-[10px] font-bold text-text-secondary uppercase tracking-wider mb-1">Minimum</div>
                    <div className="text-xl font-bold font-mono text-text-primary">{fieldStats.min.toFixed(3)}</div>
                  </div>
                  <div className="bg-secondary rounded-lg p-3 border border-border-subtle">
                    <div className="text-[10px] font-bold text-text-secondary uppercase tracking-wider mb-1">Maximum</div>
                    <div className="text-xl font-bold font-mono text-text-primary">{fieldStats.max.toFixed(3)}</div>
                  </div>
                  <div className="bg-secondary rounded-lg p-3 border border-border-subtle">
                    <div className="text-[10px] font-bold text-text-secondary uppercase tracking-wider mb-1">Std Dev</div>
                    <div className="text-xl font-bold font-mono text-text-primary">{fieldStats.stdDev.toFixed(3)}</div>
                  </div>
                  <div className="bg-secondary rounded-lg p-3 border border-border-subtle">
                    <div className="text-[10px] font-bold text-text-secondary uppercase tracking-wider mb-1">Pixels</div>
                    <div className="text-xl font-bold font-mono text-text-primary">{fieldStats.pixelCount}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Health Distribution */}
            <div className="bg-surface-card border border-border-strong rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-6">
                <FaChartLine className="w-5 h-5 text-primary" />
                <div>
                  <h3 className="text-lg font-bold text-text-primary">Health Distribution</h3>
                  <p className="text-sm text-text-secondary">Vegetation health breakdown by classification</p>
                </div>
              </div>

              <div className="space-y-5">
                {Object.entries(fieldStats.healthPercentages).map(([key, value]) => {
                  const classification = NDVI_CLASSIFICATIONS[key.toUpperCase()] ||
                    NDVI_CLASSIFICATIONS[key.replace(/([A-Z])/g, '_$1').toUpperCase()];
                  const classColor = classification?.color || '#888';
                  const label = key.replace(/([A-Z])/g, ' $1').replace('no Vegetation', 'No Vegetation').trim();
                  return (
                    <div key={key} className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium" style={{ color: classColor }}>
                          {label}
                        </span>
                        <span className="font-bold">{value}%</span>
                      </div>
                      <Progress
                        value={value}
                        className="h-4"
                        style={{
                          '--progress-background': classColor
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends" className="space-y-6">
            <div className="bg-surface-card border border-border-strong rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-6">
                <FaChartLine className="w-5 h-5 text-primary" />
                <div>
                  <h3 className="text-lg font-bold text-text-primary">Vegetation Trend Analysis</h3>
                  <p className="text-sm text-text-secondary">12-month NDVI history and forecast</p>
                </div>
              </div>
              <div className="space-y-6">
                {/* Trend Summary */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div
                    className="p-6 rounded-lg border-2"
                    style={{
                      borderColor: trendAnalysis.trend === 'improving' ? 'hsl(142 76% 36%)' :
                        trendAnalysis.trend === 'declining' ? 'hsl(0 84.2% 60.2%)' :
                          'hsl(var(--muted))',
                      backgroundColor: trendAnalysis.trend === 'improving' ? 'hsl(142 76% 36%)10' :
                        trendAnalysis.trend === 'declining' ? 'hsl(0 84.2% 60.2%)10' :
                          'transparent'
                    }}
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <div className="text-4xl">
                        {trendAnalysis.trend === 'improving' ? <FaArrowUp className="text-green-600" /> :
                          trendAnalysis.trend === 'declining' ? <FaArrowDown className="text-red-600" /> :
                            <span>→</span>}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm text-muted-foreground mb-1">Vegetation Trend</div>
                        <div className="text-2xl font-bold capitalize">{trendAnalysis.trend}</div>
                        <div className="text-sm text-muted-foreground">Slope: {trendAnalysis.slope.toFixed(3)}/period</div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Data Points</span>
                      <span className="font-semibold">{trendAnalysis.dataPoints}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Period</span>
                      <span className="font-semibold">
                        {new Date(trendAnalysis.firstDate).toLocaleDateString()} - {new Date(trendAnalysis.lastDate).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Next Forecast</span>
                      <span className="font-semibold">
                        {trendAnalysis.forecast.nextPeriod.toFixed(3)}
                        <span className="text-xs text-muted-foreground ml-1">
                          ({trendAnalysis.forecast.confidence}% confidence)
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Time Series Chart */}
                <div>
                  <h4 className="text-sm font-semibold mb-4">12-Month NDVI History</h4>
                  <div className="border rounded-lg p-4 bg-card">
                    <div className="flex items-end justify-between gap-2 h-64">
                      {timeSeries.map((data, idx) => {
                        const height = (data.ndvi * 100);
                        const color = data.ndvi >= 0.6 ? NDVI_CLASSIFICATIONS.EXCELLENT.color :
                          data.ndvi >= 0.4 ? NDVI_CLASSIFICATIONS.GOOD.color :
                            data.ndvi >= 0.2 ? NDVI_CLASSIFICATIONS.MODERATE.color :
                              NDVI_CLASSIFICATIONS.POOR.color;
                        return (
                          <Tooltip key={idx}>
                            <TooltipTrigger asChild>
                              <div className="flex-1 flex flex-col items-center gap-2 cursor-pointer group">
                                <div
                                  className="w-full rounded-t transition-all group-hover:opacity-80"
                                  style={{
                                    height: `${height}%`,
                                    backgroundColor: color,
                                    minHeight: '4px'
                                  }}
                                />
                                <span className="text-xs text-muted-foreground transform -rotate-45 origin-top-left whitespace-nowrap">
                                  {new Date(data.date).toLocaleDateString('en-US', { month: 'short' })}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{new Date(data.date).toLocaleDateString()}: {data.ndvi.toFixed(3)}</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Change Detection Tab */}
          <TabsContent value="changes" className="space-y-6">
            <div className="bg-surface-card border border-border-strong rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-6">
                <FaExclamationTriangle className="w-5 h-5 text-primary" />
                <div>
                  <h3 className="text-lg font-bold text-text-primary">Change Detection</h3>
                  <p className="text-sm text-text-secondary">Vegetation changes compared to previous period</p>
                </div>
              </div>
              <div className="space-y-6">
                {/* Change Summary */}
                <div
                  className="p-6 rounded-lg border-2"
                  style={{
                    borderColor: changeDetection.changeDirection === 'improving' ? 'hsl(142 76% 36%)' :
                      changeDetection.changeDirection === 'declining' ? 'hsl(0 84.2% 60.2%)' :
                        'hsl(var(--muted))',
                    backgroundColor: changeDetection.changeDirection === 'improving' ? 'hsl(142 76% 36%)10' :
                      changeDetection.changeDirection === 'declining' ? 'hsl(0 84.2% 60.2%)10' :
                        'transparent'
                  }}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-4xl">
                      {changeDetection.changeDirection === 'improving' ?
                        <FaArrowUp className="text-green-600" /> :
                        changeDetection.changeDirection === 'declining' ?
                          <FaArrowDown className="text-red-600" /> :
                          <span>→</span>}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-muted-foreground mb-1">Overall Change</div>
                      <div className="text-2xl font-bold">
                        {changeDetection.meanChange > 0 ? '+' : ''}{changeDetection.meanChange.toFixed(3)}
                      </div>
                      <div className="text-sm text-muted-foreground capitalize">{changeDetection.changeDirection}</div>
                    </div>
                  </div>
                </div>

                {/* Change Breakdown */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 rounded-lg border bg-green-50 dark:bg-green-950/20">
                    <FaArrowUp className="w-6 h-6 mx-auto mb-2 text-green-600" />
                    <div className="text-2xl font-bold text-green-600">
                      {changeDetection.changePercentages.improvement}%
                    </div>
                    <div className="text-sm text-muted-foreground">Improvement</div>
                  </div>
                  <div className="text-center p-4 rounded-lg border bg-muted">
                    <span className="text-2xl block mb-2">→</span>
                    <div className="text-2xl font-bold">
                      {changeDetection.changePercentages.stable}%
                    </div>
                    <div className="text-sm text-muted-foreground">Stable</div>
                  </div>
                  <div className="text-center p-4 rounded-lg border bg-red-50 dark:bg-red-950/20">
                    <FaArrowDown className="w-6 h-6 mx-auto mb-2 text-red-600" />
                    <div className="text-2xl font-bold text-red-600">
                      {changeDetection.changePercentages.decline}%
                    </div>
                    <div className="text-sm text-muted-foreground">Decline</div>
                  </div>
                </div>

                {/* Alerts */}
                {changeDetection.alerts && changeDetection.alerts.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold">Vegetation Alerts</h4>
                    {changeDetection.alerts.map((alert, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-3 p-4 rounded-lg border bg-card"
                      >
                        <div className="mt-0.5">
                          {alert.severity === 'critical' || alert.severity === 'warning' ?
                            <FaExclamationTriangle className="w-5 h-5 text-destructive" /> :
                            <FaInfoCircle className="w-5 h-5 text-primary" />
                          }
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{alert.message}</p>
                          <p className="text-xs text-muted-foreground mt-1">{alert.action}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* NDVI Classification Legend */}
        <div className="bg-surface-card border border-border-strong rounded-xl p-6 shadow-sm mt-6">
          <div className="mb-4">
            <h3 className="text-lg font-bold text-text-primary">NDVI Classification Scale</h3>
            <p className="text-sm text-text-secondary">Understanding NDVI values and vegetation health</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.values(NDVI_CLASSIFICATIONS).reverse().map((cls) => (
              <div
                key={cls.label}
                className="flex items-center gap-3 p-3 rounded-lg border border-border-subtle bg-secondary/50"
                style={{
                  borderLeftColor: cls.color,
                  borderLeftWidth: '4px'
                }}
              >
                <div
                  className="w-3 h-3 rounded-full shrink-0"
                  style={{ backgroundColor: cls.color }}
                />
                <div className="flex-1">
                  <div className="text-sm font-bold text-text-primary">{cls.label}</div>
                  <div className="text-xs font-mono text-text-secondary">
                    {cls.min.toFixed(1)} - {cls.max.toFixed(1)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Export Options */}
        <div className="flex gap-4 mt-6">
          <Button variant="outline" className="flex-1">
            <FaDownload className="w-4 h-4 mr-2" />
            Export Report (PDF)
          </Button>
          <Button variant="outline" className="flex-1">
            <FaDownload className="w-4 h-4 mr-2" />
            Download GeoTIFF
          </Button>
        </div>
      </div>
    </TooltipProvider >
  );
};

export default SatelliteNDVIPanel;
